import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useToggleHandler } from './useToggleHandler';

const useOverlayVisibility = (
  overlayRef: React.RefObject<HTMLDivElement | null>,
  isOpen: boolean
) => {
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const isCurrentlyOpen = overlay.matches(':popover-open');

    if (isOpen && !isCurrentlyOpen) {
      overlay.showPopover();
    } else if (!isOpen && isCurrentlyOpen) {
      overlay.hidePopover();
    }
  }, [overlayRef, isOpen]);
};

export type OverlayProps = {
  isOpen: boolean;
  onClose: VoidFunction;
};

/**
 * A simple overlay component that renders children in the top layer covering the entire screen.
 * Uses the Popover API to ensure content is rendered in the browser's top layer.
 *
 * Why is it implemented with showPopover() instead of showModal() as one might expect for an overlay, since
 * it covers the entire screen?
 * - showModal() _blocks interaction_ with all other content, _including other modals/popovers_, while
 *   showPopover() only blocks interaction with content beneath it in the stacking order.
 * - Both render in the top layer, but stacking order is determined by the order of show*() calls
 * - React's useEffect runs children-first, so a parent's showModal() would appear ON TOP of
 *   a child's showPopover(), blocking interaction with the child
 * - With showPopover(), we can render Overlay and Popover as siblings, controlling stacking
 *   order via render order (first = bottom, last = top) without timing hacks
 */
export const Overlay = ({
  isOpen,
  onClose,
  children,
}: React.PropsWithChildren<OverlayProps>) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useToggleHandler(overlayRef, isOpen, onClose);
  useOverlayVisibility(overlayRef, isOpen);

  return (
    <StyledOverlay ref={overlayRef} popover="manual">
      {children}
    </StyledOverlay>
  );
};

const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  border: none;
  padding: 0;
  background-color: transparent;
  overflow: visible;
`;
